// lib
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ModalOptions } from '@ionic/core';

// elvispos
import { BaseService } from 'ngx-elvis-ds';
import { ConfigService } from '../config.service';
import { OverlayDialogPage, OverlayDialogCommands } from '../gui/gui-dialogs/overlay-dialog/overlay-dialog.page';
import { ConfirmDialogPage, ConfirmDialogCommands } from '../gui/gui-dialogs/confirm-dialog/confirm-dialog.page';
import { KeyboardDialogCommands, KeyboardDialogPage } from '../gui/gui-dialogs/keyboard-dialog/keyboard-dialog.page';
import { InputDialogPage, InputDialogCommands } from '../gui/gui-dialogs/input-dialog/input-dialog.page';
import { SCOPaymentDialogPage } from '../gui/gui-dialogs/sco-payment-dialog/sco-payment-dialog.page';
import { InfoDialogPage, InfoDialogCommands } from '../gui/gui-dialogs/info-dialog/info-dialog.page';
import { PaymentDialogCommands } from '../base/payment-page';
import { ListDialogPage, ListDialogCommands } from '../gui/gui-dialogs/list-dialog/list-dialog.page';
import { SearchDialogCommands, SearchDialogPage } from '../gui/gui-dialogs/search-dialog/search-dialog.page';
import { FunctionDialogCommands, FunctionDialogPage } from '../gui/gui-dialogs/function-dialog/function-dialog.page';
import { SAMService } from '../sam/sam.service';
import { SAM } from '../sam/base/sam';
import { SAMMessage } from '../sam/interfaces/sam-message';
import { DeviceType } from '../base/device-type.enum';
import { PaymentDialogPage } from '../gui/gui-dialogs/payment-dialog/payment-dialog.page';
import { SearchDialogService } from '../gui/gui-dialogs/search-dialog/search-dialog.service';
import { PDFViewerDialogCommands, PDFViewerDialogPage } from '../gui/gui-dialogs/pdf-viewer-dialog/pdf-viewer-dialog.page';
import { MaintenanceDialogCommands, MaintenanceDialogPage } from '../gui/gui-dialogs/maintenance-dialog/maintenance-dialog.page';
import { NumericInputDialogPage } from '../gui/gui-dialogs/numeric-input-dialog/numeric-input-dialog.page';
import { ProductDetailDialogCommands, ProductDetailDialogPage } from '../gui/gui-dialogs/product-detail-dialog/product-detail-dialog.page';

@Injectable({
  providedIn: 'root'
})
export class DialogsService extends BaseService {

  private static CATCH_COMMANDS = [
    ...FunctionDialogCommands.OPEN,
    ...KeyboardDialogCommands.OPEN,
    ...ListDialogCommands.OPEN,
    ...SearchDialogCommands.OPEN,
    ...ConfirmDialogCommands.OPEN,
    ...InputDialogCommands.OPEN,
    ...OverlayDialogCommands.OPEN,
    ...InfoDialogCommands.OPEN,
    ...PaymentDialogCommands.OPEN,
    ...PDFViewerDialogCommands.OPEN,
    ...ProductDetailDialogCommands.OPEN,
    ...MaintenanceDialogCommands.OPEN,
  ];

  activeSAM: SAMService | SAM;

  dialogOpen: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private configSvc: ConfigService,
    private sam: SAMService,
    private searchSvc: SearchDialogService
  ) {
    super();
  }

  async create(dialogOptions: any, childSam?: SAM) {
    const modal = await this.modalCtrl.create(dialogOptions.ionic);

    const modalCloseListener = await this.sam.channels.dialogs.subscribe((msg: SAMMessage) => {
      if (dialogOptions.commands.close) {
        if (dialogOptions.commands.close.includes(msg.Command)) {
          if (msg.Command === 'SEARCH_DIALOG_CLOSE') {
            this.searchSvc.onSetSearchOpen(false);
          }
          this.dismiss(modal, modalCloseListener);
        }
      }
    });

    await modal.present();
    this.dialogOpen = true;
  }

  subscribeListeners() {
    this.sam.channels.dialogs.subscribe((msg: SAMMessage) => {
      if (DialogsService.CATCH_COMMANDS.includes(msg.Command)) {
        this.activeSAM = this.sam;
        const data: any = msg.parms;

        if (msg.Command === ProductDetailDialogCommands.OPEN[0] && data.url) {
          window.open(data.url);
          return;
        }

        const options = this.initModal(msg.Command, data);
        options.ionic.componentProps = { data };
        if (msg.Command === 'SEARCH_DIALOG_OPEN') {
          this.searchSvc.onSetSearchOpen(false);
        }

        this.create(options);
      }
    });
  }

  // private

  private initModal(command: string, data: any) {
    const options: {
      ionic: ModalOptions,
      commands: any
    } = {
      ionic: {
        component: null,
        backdropDismiss: false,
      },
      commands: {
        close: [],
        update: [],
      }
    };

    switch (command) {
      case ConfirmDialogCommands.OPEN[0]:
        options.ionic.component = ConfirmDialogPage;
        options.ionic.cssClass = 'confirm-dialog fullscreen';
        options.commands.close = ConfirmDialogCommands.CLOSE;
        break;
      case InfoDialogCommands.OPEN[0]:
        options.ionic.component = InfoDialogPage;
        options.ionic.cssClass = 'info-dialog fullscreen';
        options.commands.close = InfoDialogCommands.CLOSE;
        break;
      case OverlayDialogCommands.OPEN[0]:
        options.ionic.component = OverlayDialogPage;
        options.ionic.cssClass = 'overlay-dialog fullscreen';
        options.commands.close = OverlayDialogCommands.CLOSE;
        break;
      case InputDialogCommands.OPEN[0]:
        options.ionic.component = this.getInputPageFromData(data);
        options.ionic.cssClass = 'input-dialog fullscreen';
        options.commands.close = InputDialogCommands.CLOSE;
        break;
      case PaymentDialogCommands.OPEN[0]:
        if (
          this.configSvc.deviceType === DeviceType.SCO ||
          this.configSvc.deviceType === DeviceType.BAR_SELF
        ) {
          options.ionic.component = SCOPaymentDialogPage;
        } else {
          options.ionic.component = PaymentDialogPage;
        }
        options.ionic.cssClass = 'payment-dialog fullscreen';
        options.commands.close = PaymentDialogCommands.CLOSE;
        break;
      case SearchDialogCommands.OPEN[0]:
        options.ionic.component = SearchDialogPage;
        options.ionic.cssClass = 'search-dialog fullscreen';
        options.commands.close = SearchDialogCommands.CLOSE;
        this.searchSvc.clean();
        break;
      case KeyboardDialogCommands.OPEN[0]:
      case KeyboardDialogCommands.OPEN[1]:
        options.ionic.component = KeyboardDialogPage;
        options.ionic.cssClass = 'keyboard-dialog fullscreen';
        options.commands.close = KeyboardDialogCommands.CLOSE;
        break;
      case ListDialogCommands.OPEN[0]:
        options.ionic.component = ListDialogPage;
        options.ionic.cssClass = 'list-dialog fullscreen';
        options.commands.close = ListDialogCommands.CLOSE;
        break;
      case FunctionDialogCommands.OPEN[0]:
        options.ionic.component = FunctionDialogPage;
        options.ionic.cssClass = 'function-dialog';
        options.commands.close = FunctionDialogCommands.CLOSE;
        break;
      case PDFViewerDialogCommands.OPEN[0]:
        options.ionic.component = PDFViewerDialogPage;
        options.ionic.cssClass = 'pdf-viewer-dialog fullscreen';
        options.commands.close = PDFViewerDialogCommands.CLOSE;
        break;
      case ProductDetailDialogCommands.OPEN[0]:
        options.ionic.component = ProductDetailDialogPage;
        options.ionic.cssClass = 'product-detail-dialog fullscreen';
        options.commands.close = ProductDetailDialogCommands.CLOSE;
        break;
      case MaintenanceDialogCommands.OPEN[0]:
        options.ionic.component = MaintenanceDialogPage;
        options.ionic.cssClass = 'maintenance-dialog fullscreen';
        options.commands.close = MaintenanceDialogCommands.CLOSE;
        break;
    }

    return options;
  }

  private async dismiss(modal, listener) {
    if (modal) { await modal.dismiss(); }
    if (listener) { listener.unsubscribe(); }
    this.dialogOpen = false;
  }

  private getInputPageFromData(data: any) {
    if ( ![ 'CURRENCY', 'NUMERIC', 'NUMERICCURRENCY', 'FLOAT', 'PERC' ].includes(data.Type) ) {
      return InputDialogPage;
    }

    return NumericInputDialogPage;
  }
}
